import { useTranslation } from "react-i18next";
import { StyleSheet } from "react-native";
import { Text } from "react-native-paper";
import Svg, { Path } from "react-native-svg";
import { useAppTheme } from "../../useAppTheme";
import Box from "../Box";

type Place = 1 | 2 | 3;

export const PODIUM_BLOCK_BEVEL_HEIGHT = 15;
const BEVEL_HEIGHT = PODIUM_BLOCK_BEVEL_HEIGHT;
const VIEWBOX_WIDTH = 148.667;
const BEVEL_INSET = 10.9314;
const INNER_EDGE = VIEWBOX_WIDTH - BEVEL_INSET;

export const PODIUM_BLOCK_HEIGHTS: Record<Place, number> = {
	1: 152,
	2: 96,
	3: 84,
};

const BEVEL_PATHS: Record<Place, string> = {
	1: `M${BEVEL_INSET} 0 H${INNER_EDGE} L${VIEWBOX_WIDTH} ${BEVEL_HEIGHT} H0 Z`,
	2: `M${BEVEL_INSET} 0 H${VIEWBOX_WIDTH} V${BEVEL_HEIGHT} H0 Z`,
	3: `M0 0 H${INNER_EDGE} L${VIEWBOX_WIDTH} ${BEVEL_HEIGHT} H0 Z`,
};

const BEVEL_FADE = 0.65;

const PLACE_LABEL_KEYS: Record<Place, string> = {
	1: "leaderboard.podium.first",
	2: "leaderboard.podium.second",
	3: "leaderboard.podium.third",
};

type Props = {
	place: Place;
};

export const LeaderboardPodiumBlock = ({ place }: Props) => {
	const theme = useAppTheme();
	const { t } = useTranslation();

	const palette: Record<Place, { body: string; label: string }> = {
		1: {
			body: theme.colors.secondary,
			label: theme.colors.onPrimary,
		},
		2: {
			body: theme.colors.inversePrimary,
			label: theme.colors.onSurfaceVariant,
		},
		3: {
			body: theme.colors.elevation.level5,
			label: theme.colors.onSurfaceVariant,
		},
	};

	const { body, label } = palette[place];
	const isFirst = place === 1;

	return (
		<Box width="100%">
			<Svg
				width="100%"
				height={BEVEL_HEIGHT}
				viewBox={`0 0 ${VIEWBOX_WIDTH} ${BEVEL_HEIGHT}`}
				preserveAspectRatio="none"
			>
				<Path d={BEVEL_PATHS[place]} fill={body} fillOpacity={BEVEL_FADE} />
			</Svg>
			<Box
				height={PODIUM_BLOCK_HEIGHTS[place]}
				alignItems="center"
				justifyContent="center"
				paddingTop={12}
				backgroundColor={body}
			>
				<Text
					variant={isFirst ? "titleMedium" : "bodySmall"}
					style={[styles.label, { color: label }]}
				>
					{t(PLACE_LABEL_KEYS[place])}
				</Text>
			</Box>
		</Box>
	);
};

const styles = StyleSheet.create({
	label: {
		fontWeight: "600",
	},
});
